<script setup lang="ts">
    import { computed, inject, onMounted, reactive, ref, toRaw } from 'vue';
    import axios from 'axios';
    import ConfirmationModal from '@/components/ConfirmationModal.vue';
    import CustomPagination from '@/components/CustomPagination.vue';
    import CustomButton from '@/components/CustomButton.vue';
    import ImportMembresComponent from '@/components/ImportMembresComponent.vue';
    import authService from '@/services/authService';
    import { showAlert } from '@/utils/swal';

    const BACKEND_URL = inject('BACKEND_URL');

    const refreshKey = ref(0);
    const errorMessage = ref('');
    const loading = ref(false);
    const loadingExportToExcel = ref(false);
    const loadingExportToPdf = ref(false);
    const loadingInvitations = ref(false);
    const itemsList = ref<any[]>([]);
    const confirmationModalRef = ref<InstanceType<typeof ConfirmationModal> | null>(null);
    const selectedItems = ref<number[]>([]);
    const collectiviteId = ref<number | null>(null);

    const areAllItemsSelected = computed(() => {
        return itemsList.value.length > 0 && itemsList.value.length === selectedItems.value.length;
    });

    const startupInfos = reactive({
        pays: [] as any[],
    });

    const queryParams = reactive({
        pageNumber: 1,
        sortDir: 'ASC',
        sortColumn: 'fullname',
        sortOption: 'fullname_asc',
        borneInf: '',
        borneSup: '',
        pays_id: '',
        search: '',
        trashed: 0,
    });

    const pagination = reactive({
        total: 0,
        per_page: 10,
        last_page: 1,
    });

    const SORT_OPTIONS = [
        { value: 'fullname_asc', label: 'Nom & Prénoms (ASC)', sort_column: 'fullname', sort_direction: 'ASC' },
        { value: 'fullname_desc', label: 'Nom & Prénoms (DESC)', sort_column: 'fullname', sort_direction: 'DESC' },
        { value: 'created_at_asc', label: 'Date (ASC)', sort_column: 'created_at', sort_direction: 'ASC' },
        { value: 'created_at_desc', label: 'Date (DESC)', sort_column: 'created_at', sort_direction: 'DESC' },
    ];

    const showAdvancedFilters = ref(false);

    onMounted(async () => {
        collectiviteId.value = await resolveCollectiviteId();
        if (!collectiviteId.value) {
            errorMessage.value = 'Aucune collectivité associée à votre compte';
            return;
        }
        await loadStartupInfos();
        await doListItems();
    });

    async function resolveCollectiviteId(): Promise<number | null> {
        let currentUser: any = authService.getCurrentUser();

        if (currentUser && !currentUser.collectivite_id && !currentUser.collectivite?.id && !currentUser.collectiviteId) {
            try {
                const profileResponse = await authService.getProfile();
                if (profileResponse.status === 0 && profileResponse.data) {
                    currentUser = profileResponse.data;
                    const TokenStorage = (await import('@/services/apiConfig')).TokenStorage;
                    TokenStorage.setUser(currentUser);
                }
            } catch (profileError) {
                console.error('Erreur lors de la récupération du profil:', profileError);
            }
        }

        return currentUser?.collectivite_id || currentUser?.collectiviteId || currentUser?.collectivite?.id || null;
    }

    async function loadStartupInfos() {
        try {
            const response = await axios.get('/backoffice/users/get_startupinfos_list_datas');
            startupInfos.pays = response.data.pays || [];
        } catch (error: any) {
            console.error('Erreur lors du chargement des données de démarrage:', error);
        }
    }

    async function doListItems() {
        if (!collectiviteId.value) return;
        try {
            loading.value = true;
            errorMessage.value = '';
            const queryParamsRaw = toRaw(queryParams);
            const params = {
                ...Object.fromEntries(Object.entries(queryParamsRaw).map(([k, v]) => [k, String(v)])),
                pageSize: String(pagination.per_page),
            };
            const queryString = new URLSearchParams(params).toString();
            const httpResponse = await axios.get(`/collectivites/membres/list?${queryString}`);
            itemsList.value = httpResponse.data.data || [];
            pagination.total = httpResponse.data.total || 0;
            pagination.per_page = httpResponse.data.per_page || 10;
            pagination.last_page = httpResponse.data.last_page || 1;
            selectedItems.value = [];
        } catch (error: any) {
            errorMessage.value = error.response?.data?.message || error.message || 'Erreur lors du chargement des membres';
        } finally {
            loading.value = false;
        }
    }

    async function doToggleEtatValideAction(itemsIds: number[]) {
        const confirmation = await confirmationModalRef.value?.open(
            'Membres',
            "Voulez-vous vraiment modifier l'état des éléments sélectionnés ?"
        );
        if (!confirmation) return;

        try {
            loading.value = true;
            errorMessage.value = '';
            await axios.post('/backoffice/users/toggle_etat_valide', { ids: itemsIds });

            itemsIds.forEach((itemId) => {
                const index = itemsList.value.findIndex((i) => i.id == itemId);
                if (index > -1) {
                    itemsList.value[index].actif = itemsList.value[index].actif == 1 ? 0 : 1;
                }
            });
        } catch (error: any) {
            errorMessage.value = error.response?.data?.message || error.message || 'Erreur lors de la modification';
        } finally {
            loading.value = false;
        }
    }

    async function doExportToExcelAction() {
        try {
            loadingExportToExcel.value = true;
            errorMessage.value = '';
            const queryParamsRaw = toRaw(queryParams);
            const params = {
                ...Object.fromEntries(Object.entries(queryParamsRaw).map(([k, v]) => [k, String(v)])),
                collectivite_id: String(collectiviteId.value),
            };
            const queryString = new URLSearchParams(params).toString();
            const response = await axios.get(`/backoffice/users/export_excel?${queryString}`, { responseType: 'blob' });
            const blob = new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', 'membres.xlsx');
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch (error: any) {
            errorMessage.value = error.response?.data?.message || error.message || "Erreur lors de l'export Excel";
        } finally {
            loadingExportToExcel.value = false;
        }
    }

    async function doExportToPdfAction() {
        try {
            loadingExportToPdf.value = true;
            errorMessage.value = '';
            const queryParamsRaw = toRaw(queryParams);
            const params = {
                ...Object.fromEntries(Object.entries(queryParamsRaw).map(([k, v]) => [k, String(v)])),
                collectivite_id: String(collectiviteId.value),
            };
            const queryString = new URLSearchParams(params).toString();
            const response = await axios.get(`/backoffice/users/export_pdf?${queryString}`, { responseType: 'blob' });
            const blob = new Blob([response.data], { type: 'application/pdf' });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', 'membres.pdf');
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch (error: any) {
            errorMessage.value = error.response?.data?.message || error.message || "Erreur lors de l'export PDF";
        } finally {
            loadingExportToPdf.value = false;
        }
    }

    function handleCheckAll(event: Event) {
        const checked = (event.target as HTMLInputElement).checked;
        selectedItems.value = checked ? itemsList.value.map((i) => i.id) : [];
    }

    function onFiltersChanged() {
        const sortItem = SORT_OPTIONS.find((o) => o.value == queryParams.sortOption);
        if (sortItem) {
            queryParams.sortColumn = sortItem.sort_column;
            queryParams.sortDir = sortItem.sort_direction;
        }
        queryParams.pageNumber = 1;
        doListItems();
    }

    function onPaginate(pageNumber: number) {
        queryParams.pageNumber = pageNumber;
        doListItems();
    }

    function toggleAdvancedFilters() {
        showAdvancedFilters.value = !showAdvancedFilters.value;
    }

    async function downloadCsv() {
        try {
            const response = await axios.get('/collectivites/membres/download_csv_template', { responseType: 'blob' });
            const blob = new Blob([response.data], { type: 'text/csv' });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', 'collectivite_membres_template.csv');
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error('Erreur de téléchargement :', error);
        }
    }

    function onMembersImported() {
        refreshKey.value++;
        doListItems();
    }

    async function sendInvitationsUnverified() {
        if (!collectiviteId.value) return;

        try {
            const countResponse = await axios.get('/collectivites/membres/count_unverified');
            const unverifiedCount = countResponse.data.count || 0;

            if (unverifiedCount === 0) {
                showAlert.info('Aucun membre non vérifié trouvé pour cette collectivité');
                return;
            }

            const confirmation = await confirmationModalRef.value?.open(
                'Envoyer les invitations',
                `Vous êtes sur le point d'envoyer ${unverifiedCount} invitation(s) aux membres non vérifiés de cette collectivité. Voulez-vous continuer ?`
            );

            if (!confirmation) return;

            loadingInvitations.value = true;
            const response = await axios.post('/collectivites/membres/send_invitations_unverified', {
                collectivite_id: collectiviteId.value,
            });
            showAlert.success(response.data.message || 'Invitations envoyées avec succès');

            setTimeout(() => {
                doListItems();
            }, 1000);
        } catch (error: any) {
            console.error("Erreur lors de l'envoi des invitations:", error);
            showAlert.error(error.response?.data?.message || "Une erreur est survenue lors de l'envoi des invitations");
        } finally {
            loadingInvitations.value = false;
        }
    }
</script>

<template>
    <section class="container-full-app bg-grey-50">
        <section class="s20">
            <div v-if="collectiviteId" class="mb-5">
                <import-membres-component
                    :key="refreshKey"
                    @completed="onMembersImported"
                />
            </div>

            <div v-if="errorMessage" class="alert alert-danger mb-4">
                {{ errorMessage }}
            </div>

            <div class="widget shadow-100 s20 pb-2">
                <div class="container-full-2 s20 my-5">
                    <div class="members-toolbar">
                        <h4 class="font-semibold" style="margin: 0;">Membres <span class="text-sm text-grey-500">(recent)</span></h4>
                        <div class="members-toolbar-actions">
                        <button type="button" @click="downloadCsv" class="btn normal ml-3 justify-content-center btn-filtre">
                            <img src="/public/icons/icon-telecharger.svg" class="btn-icon" alt="" />
                            <span>Modèle CSV</span>
                        </button>
                        <label for="file-upload-membres-list" class="btn normal ml-3 justify-content-center btn-filtre" style="cursor: pointer;">
                            <img src="/public/icons/icon-upload.svg" class="btn-icon" alt="" />
                            <span>Importer CSV</span>
                        </label>
                        <button type="button" @click="sendInvitationsUnverified" class="btn normal ml-3 justify-content-center btn-filtre" :disabled="loadingInvitations">
                            <img src="/public/icons/icon-send.svg" class="btn-icon" alt="" />
                            <span v-if="!loadingInvitations">Envoyer invitations</span>
                            <span v-else>Envoi...</span>
                        </button>
                        <div class="form-field-floating default sm s4_mobile ml-3">
                            <select class="form-input-select" id="filters_sort" v-model="queryParams.sortOption" @change="onFiltersChanged">
                                <option :value="option.value" v-for="(option, index) in SORT_OPTIONS" :key="'filter_sort_' + index">{{ option.label }}</option>
                            </select>
                        </div>
                        <button @click="toggleAdvancedFilters" class="btn normal white2 s3_mobile ml-4 justify-content-center btn-filtre btn-filter">
                            <img src="/public/icons/icon-filter.svg" class="btn-icon" alt="" />
                            <span>Options</span>
                        </button>
                        </div>
                    </div>

                    <div class="custom-filter-set-area" v-show="showAdvancedFilters">
                        <div class="grid-filter6 mt-4 mb-0">
                            <div class="form-field-floating reactive sm">
                                <input type="text" class="form-input" id="filters_search" v-model="queryParams.search" placeholder="" @keyup.enter="onFiltersChanged" />
                                <label for="filters_search">Recherche</label>
                            </div>
                            <div class="form-field-floating reactive sm">
                                <input type="date" class="form-input" id="filters_borne_inf" v-model="queryParams.borneInf" placeholder="" @change="onFiltersChanged" />
                                <label for="filters_borne_inf">Date de création >=</label>
                            </div>
                            <div class="form-field-floating reactive sm">
                                <input type="date" class="form-input" id="filters_borne_sup" v-model="queryParams.borneSup" placeholder="" @change="onFiltersChanged" />
                                <label for="filters_borne_sup">Date de création &lt;=</label>
                            </div>
                            <div class="form-field-floating sm">
                                <select class="form-input-select" id="filters_pays" v-model="queryParams.pays_id" @change="onFiltersChanged">
                                    <option value="">Tous les pays</option>
                                    <option :value="option.id" v-for="(option, index) in startupInfos.pays" :key="'filter_pays_' + index">{{ option.nom }}</option>
                                </select>
                                <label for="filters_pays">Pays <b class="color-danger">*</b></label>
                            </div>
                            <div class="form-field-floating sm">
                                <select class="form-input-select" id="filters_trashed" v-model="queryParams.trashed" @change="onFiltersChanged">
                                    <option :value="0">Membres valides</option>
                                    <option :value="1">Membres en sommeil</option>
                                </select>
                                <label for="filters_trashed">Etat <b class="color-danger">*</b></label>
                            </div>
                            <custom-button classes-list="btn black normal mx-0 btn-filtre" :loading="loadingExportToExcel" @click="doExportToExcelAction">
                                <img src="/public/icons/icon-excel.svg" class="btn-icon" alt="" />
                                <span>Exporter Excel</span>
                            </custom-button>
                            <custom-button classes-list="btn black normal mx-0 btn-filtre" :loading="loadingExportToPdf" @click="doExportToPdfAction">
                                <img src="/public/icons/icon-pdf.svg" class="btn-icon" alt="" />
                                <span>Exporter PDF</span>
                            </custom-button>
                        </div>
                    </div>
                </div>
            </div>

            <div class="members-table-wrapper">
                <table class="table separator skin3 selection rounded">
                    <thead>
                        <tr>
                            <th>
                                <label class="btn-circle" for="check-list1">
                                    <input type="checkbox" id="check_all" :checked="areAllItemsSelected" @change="handleCheckAll" />
                                </label>
                            </th>
                            <th></th>
                            <th><img src="/public/icons/icon-filter-up.svg" class="size-xxs mt--1" alt="" /> Information</th>
                            <th><img src="/public/icons/icon-filter-up.svg" class="size-xxs mt--1" alt="" /> Utilisation</th>
                            <th>Collectivité</th>
                            <th class="flex-right">
                                <button class="btn-ultra color2">
                                    <div class="btn-show">
                                        <img src="/public/icons/icon-actif.svg" class="btn-icon" alt="" />
                                        <span>Actif</span>
                                    </div>
                                    <div class="btn-loading">
                                        <div class="loader-dots"></div>
                                    </div>
                                </button>
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        <template v-if="loading">
                            <tr>
                                <td colspan="6">
                                    <div class="text-center text-grey-700 py-5">
                                        <div class="loader dark"></div>
                                    </div>
                                </td>
                            </tr>
                        </template>
                        <template v-else-if="itemsList.length == 0">
                            <tr>
                                <td colspan="6">
                                    <div class="text-center text-grey-700 py-5">Aucun élément trouvé</div>
                                </td>
                            </tr>
                        </template>

                        <tr class="text-grey-700 over-effect" v-for="(item, index) in itemsList" :key="'item_' + index">
                            <td class="selection-item">
                                <label class="btn-circle" :for="'item_check_' + index">
                                    <input type="checkbox" name="selectedItems[]" :id="'item_check_' + index" :value="item.id" v-model="selectedItems" />
                                </label>
                            </td>
                            <td class="bg-white">
                                <div class="member-avatars">
                                    <div class="member-avatar">
                                        <img :src="item.photo_url || item.avatar_url || '/public/assets/users/300w/user2.jpg'" alt="" />
                                    </div>
                                    <div v-if="item.collectivite" class="member-avatar member-avatar-logo">
                                        <img :src="item.collectivite?.logo ? (BACKEND_URL + '/assets/collectivites/' + item.collectivite.logo) : '/public/assets/logos/logo2.svg'" alt="" />
                                    </div>
                                </div>
                            </td>
                            <td style="width: 20%;">
                                <p class="text-xl font-semibold mb-2">
                                    {{ item.nom + ' ' + item.prenom }} &nbsp;
                                    <span v-if="!item.email_verified_at" class="tag-state warning stroke">En cours</span>
                                    <span v-if="item.email_verified_at" class="tag-state success stroke">Vérifié</span>
                                    <span v-if="item.actif == 1" class="tag-state notif stroke">Actif</span>
                                    <span v-if="item.actif == 0" class="tag-state danger stroke">Inactif</span>
                                </p>
                                <p class="text-sm text-grey-600">{{ item.email }}</p>
                                <p class="text-sm text-grey-600">{{ item.telephone }}</p>
                            </td>
                            <td class="text-3xl" style="width: 10%;">
                                <template v-if="item.quotite_cessible_pourcent">
                                    {{ item.quotite_cessible_pourcent }}%
                                </template>
                                <template v-else>-</template>
                                <p class="mt-1 text-sm">
                                    <template v-if="item.quotite_cessible_montant">
                                        {{ new Intl.NumberFormat('fr-FR').format(item.quotite_cessible_montant) }} F
                                    </template>
                                    <template v-else>-</template>
                                </p>
                            </td>
                            <td>
                                {{ item.collectivite?.nom || 'N/A' }}
                            </td>
                            <td>
                                <div class="flex-right gap-4">
                                    <router-link :to="'/front/collectivites/membres/' + item.id + '/edit'">
                                        <button class="btn tips">
                                            <img src="/public/icons/icon-edit.svg" class="btn-icon" alt="" />
                                            <span>Editer</span>
                                        </button>
                                    </router-link>
                                    <custom-button :classes-list="'btn-ultra color2 tips'" @click="doToggleEtatValideAction([item.id])" v-if="item.deleted_at == null && item.actif == 1">
                                        <img src="/public/icons/icon-actif.svg" class="btn-icon" alt="" />
                                        <span>Actif</span>
                                    </custom-button>
                                    <custom-button :classes-list="'btn-ultra color2 tips'" @click="doToggleEtatValideAction([item.id])" v-if="item.deleted_at == null && item.actif == 0">
                                        <img src="/public/icons/icon-inactif.svg" class="btn-icon" alt="" />
                                        <span>Inactif</span>
                                    </custom-button>
                                    <router-link :to="'/front/collectivites/membres/' + item.id">
                                        <button class="btn white2 active">
                                            <img src="/public/icons/icon-consulter.svg" class="btn-icon" alt="" />
                                            <span>Gérer</span>
                                        </button>
                                    </router-link>
                                    <router-link :to="'/front/collectivites/membres/' + item.id + '/carte'">
                                        <button class="btn white2 active">
                                            <img src="/public/icons/icon-consulter.svg" class="btn-icon" alt="" />
                                            <span>Carte</span>
                                        </button>
                                    </router-link>
                                    <router-link :to="'/front/collectivites/membres/' + item.id + '/utilisation'">
                                        <button class="btn white2 active">
                                            <img src="/public/icons/icon-consulter.svg" class="btn-icon" alt="" />
                                            <span>Utilisation</span>
                                        </button>
                                    </router-link>
                                    <router-link :to="'/front/collectivites/membres/' + item.id + '/plan'">
                                        <button class="btn white2 active">
                                            <img src="/public/icons/icon-consulter.svg" class="btn-icon" alt="" />
                                            <span>Plan</span>
                                        </button>
                                    </router-link>
                                    <router-link :to="'/front/collectivites/membres/' + item.id + '/revolving'">
                                        <button class="btn white2 active">
                                            <img src="/public/icons/icon-consulter.svg" class="btn-icon" alt="" />
                                            <span>Revolving</span>
                                        </button>
                                    </router-link>
                                </div>
                            </td>
                        </tr>
                    </tbody>
                </table>

                <div style="display: flex; flex-direction: row; justify-content: center;" v-if="pagination.total > 0">
                    <custom-pagination
                        v-model="queryParams.pageNumber"
                        :total="pagination.total"
                        :per-page="pagination.per_page"
                        :last_page="pagination.last_page"
                        @update:modelValue="onPaginate"
                    />
                </div>
            </div>
            <div class="clear-both h-30"></div>
        </section>

        <div class="clear-both h-30"></div>
        <confirmation-modal ref="confirmationModalRef" />
    </section>
</template>

<style scoped>
.members-toolbar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
    flex-wrap: wrap;
}

.members-toolbar-actions {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    flex-wrap: wrap;
}

.members-table-wrapper {
    overflow-x: auto;
    width: 100%;
}

.members-table-wrapper > table {
    min-width: 1100px;
}

.member-avatars {
    display: flex;
    align-items: center;
    gap: 0.75rem;
}

.member-avatar {
    width: 56px;
    height: 56px;
    border-radius: 15px;
    overflow: hidden;
    background-color: rgba(var(--color-warning-rgb), 0.2);
    flex-shrink: 0;
}

.member-avatar img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
}

.member-avatar-logo {
    background-color: rgba(var(--color-black-rgb), 0.08);
    padding: 0.5rem;
}

.member-avatar-logo img {
    object-fit: contain;
}
</style>
